import type { UiAction } from "./input";
import type { PowerupKind } from "./types";
import { POWERUP_COSTS } from "./powerupConfig";
import { TRAP_CONFIGS } from "../versus/versusConstants";

// â”€â”€ Constants â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export const RADIAL_HOLD_THRESHOLD_MS = 150;
export const RADIAL_RADIUS_PX = 100;
export const RADIAL_ITEM_SIZE_PX = 44;
export const RADIAL_DEAD_ZONE_PX = 20;
/** Duration of fade-in animation in ms */
export const RADIAL_FADE_IN_MS = 150;

// â”€â”€ Types â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export interface RadialMenuItem {
  id: string;
  label: string;
  action: UiAction;
  angle: number; // radians, 0 = right, PI/2 = down
  cost: number;
  enabled: boolean;
}

export type RadialMenuKind = "powerup" | "trap";

export interface RadialMenuState {
  kind: RadialMenuKind;
  active: boolean;
  hubX: number;
  hubY: number;
  items: RadialMenuItem[];
  highlightedIndex: number | null;
  /** 0..1 fade-in progress */
  openProgress: number;
  holdStartMs: number;
  pointerId: number;
}

// â”€â”€ Angle computation â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

/**
 * Returns an array of 5 angles in radians for a wide arc layout.
 *
 * "bottom-left" hub (powerups) â†’ arc from 12 o'clock to 4 o'clock:
 *   angles from -90Â° to +30Â° (120Â° arc, 30Â° spacing)
 *
 * "bottom-right" hub (traps) â†’ arc from 8 o'clock to 12 o'clock:
 *   angles from -210Â° to -90Â° (mirror of powerups)
 */
export function computeItemAngles(hubCorner: "bottom-left" | "bottom-right"): number[] {
  if (hubCorner === "bottom-left") {
    // 12â†’4 o'clock: -90Â° to +30Â° in 5 evenly spaced steps (30Â° apart)
    const degs = [-90, -60, -30, 0, 30];
    return degs.map((d) => (d * Math.PI) / 180);
  }
  // 8â†’12 o'clock: -210Â° to -90Â° in 5 evenly spaced steps (30Â° apart)
  const degs = [-210, -180, -150, -120, -90];
  return degs.map((d) => (d * Math.PI) / 180);
}

// â”€â”€ Highlight resolution â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

/**
 * Given hub center and pointer position, returns the index of the closest
 * item whose angular distance is within a threshold, or null if in dead zone
 * or no close match.
 */
export function resolveHighlightedIndex(
  hubX: number,
  hubY: number,
  pointerX: number,
  pointerY: number,
  items: RadialMenuItem[]
): number | null {
  const dx = pointerX - hubX;
  const dy = pointerY - hubY;
  const dist = Math.sqrt(dx * dx + dy * dy);

  if (dist < RADIAL_DEAD_ZONE_PX) return null;
  if (items.length === 0) return null;

  const pointerAngle = Math.atan2(dy, dx);

  let bestIndex: number | null = null;
  let bestAngleDist = Infinity;

  for (let i = 0; i < items.length; i++) {
    if (!items[i].enabled) continue;
    let angleDiff = items[i].angle - pointerAngle;
    // Normalize to -PI..PI
    while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
    while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;
    const absDiff = Math.abs(angleDiff);
    if (absDiff < bestAngleDist) {
      bestAngleDist = absDiff;
      bestIndex = i;
    }
  }

  // Only accept if within ~45Â° of the closest item
  const MAX_ANGLE_DIST = (45 * Math.PI) / 180;
  if (bestAngleDist > MAX_ANGLE_DIST) return null;

  return bestIndex;
}

// â”€â”€ Item builders â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const POWERUP_ORDER: Array<{ kind: PowerupKind; label: string; action: UiAction }> = [
  { kind: "HINT", label: "HINT", action: "ARM_POWERUP_HINT" },
  { kind: "ROWRIP", label: "ROW", action: "ARM_POWERUP_ROWRIP" },
  { kind: "COLCUT", label: "COL", action: "ARM_POWERUP_COLCUT" },
  { kind: "TRANSMUTE", label: "TRN", action: "ARM_POWERUP_TRANSMUTE" },
  { kind: "ZIGZAGBOLT", label: "ZIG", action: "ARM_POWERUP_ZIGZAGBOLT" }
];

const TRAP_ORDER: Array<{ id: string; label: string; action: UiAction; configKey: string }> = [
  { id: "LOCKDOWN", label: "LOCK", action: "TRAP_LOCKDOWN", configKey: "LOCKDOWN_CHAINS" },
  { id: "FOG", label: "FOG", action: "TRAP_FOG", configKey: "FOG_OVERLAY" },
  { id: "COIN_TAX", label: "TAX", action: "TRAP_COIN_TAX", configKey: "COIN_TAX" },
  { id: "GROOVE_DRAIN", label: "DRAIN", action: "TRAP_GROOVE_DRAIN", configKey: "GROOVE_DRAIN" },
  { id: "DOOM_SURGE", label: "DOOM", action: "TRAP_DOOM_SURGE", configKey: "DOOM_SURGE" }
];

export function createPowerupRadialItems(
  inventory: Partial<Record<PowerupKind, number>> | undefined,
  cashBalance: number
): RadialMenuItem[] {
  const angles = computeItemAngles("bottom-left");
  return POWERUP_ORDER.map((p, i) => {
    const inv = Math.max(0, Math.floor(inventory?.[p.kind] ?? 0));
    const cost = POWERUP_COSTS[p.kind];
    const affordable = cashBalance >= cost;
    return {
      id: p.kind,
      label: p.label,
      action: p.action,
      angle: angles[i],
      cost,
      enabled: inv > 0 || affordable
    };
  });
}

export function createTrapRadialItems(
  localCash: number,
  cooldownSec: number,
  trapsEnabled: boolean
): RadialMenuItem[] {
  const angles = computeItemAngles("bottom-right");
  return TRAP_ORDER.map((t, i) => {
    const cfg = TRAP_CONFIGS[t.configKey as keyof typeof TRAP_CONFIGS];
    const affordable = localCash >= cfg.cost;
    const offCooldown = cooldownSec <= 0;
    return {
      id: t.id,
      label: t.label,
      action: t.action,
      angle: angles[i],
      cost: cfg.cost,
      enabled: trapsEnabled && affordable && offCooldown
    };
  });
}

// â”€â”€ Open progress animation â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export function updateRadialOpenProgress(state: RadialMenuState, dtMs: number): RadialMenuState {
  if (!state.active) return state;
  const newProgress = Math.min(1, state.openProgress + dtMs / RADIAL_FADE_IN_MS);
  return { ...state, openProgress: newProgress };
}
